import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './interceptors/auth.interceptor';
import { errorInterceptor } from './interceptors/error.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { provideSpinnerConfig } from 'ngx-spinner';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormlyInputComponent } from './shared/components/formly-input/formly-input.component';
import { FormlySelectComponent } from './shared/components/formly-select/formly-select.component';
import { SectionWrapperComponent } from './shared/components/section-wrapper/section-wrapper.component';
import { RepeatTypeComponent } from './shared/components/repeat-type/repeat-type.component';

const apiUrlPattern = new RegExp(`^${environment.apiUrl}(\\/.*)?$`, 'i');
const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: apiUrlPattern,
  bearerPrefix: 'Bearer',
});

const fieldTypes = [
  {
    name: 'input',
    component: FormlyInputComponent,
  },
  {
    name: 'select',
    component: FormlySelectComponent,
  },
  {
    name: 'repeat',
    component: RepeatTypeComponent,
  },
];

const fieldWrappers = [
  {
    name: 'section',
    component: SectionWrapperComponent,
  },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm: 'agents',
        clientId: 'agency-portal',
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      },
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlCondition],
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor, authInterceptor, errorInterceptor])
    ),
    provideAnimations(),
    provideToastr({
      positionClass: 'toast-bottom-center',
      timeOut: 3000,
      closeButton: true,
      preventDuplicates: true,
    }),
    provideSpinnerConfig({
      type: 'ball-clip-rotate-multiple',
    }),
    provideCharts(withDefaultRegisterables()),
    importProvidersFrom(
      NgxDaterangepickerMd.forRoot(),
      FormlyModule.forRoot({
        types: fieldTypes,
        wrappers: fieldWrappers,
      }),
      FormlyBootstrapModule
    ),
  ],
};
